/**
 * Auth Redirect Utilities
 *
 * Centralizes redirect URL parsing and validation for the authentication flow.
 * Used by: HomeRoute, Login, OAuthCallback, AuthRedirect
 *
 * SECURITY: Only allows /internal/* paths to prevent open redirect vulnerabilities.
 */

import {
  clearProcessingRequestRedirect,
  readProcessingRequestRedirect,
  storeProcessingRequestRedirect
} from '@/services/processingRequestAuthRedirect';

const DEFAULT_DESTINATION = '/internal';

/**
 * Validates a redirect path is safe (internal only).
 * SECURITY: Prevents open redirect attacks. Accepts path-relative URLs whose
 * pathname is `/internal` or starts with `/internal/`. Search and hash are
 * preserved (so email deep links like `/internal?section=recruiting&candidate=X`
 * round-trip through the auth flow).
 *
 * Rejects:
 *   - Schemes (http:, javascript:, data:)
 *   - Protocol-relative URLs (//evil.com)
 *   - Backslash protocol-relative variants (/\evil.com) — the WHATWG URL
 *     parser treats `\` as `/` in special schemes, so `/\evil.com/internal`
 *     would otherwise normalize to `http://evil.com/internal` with pathname
 *     `/internal` and slip past a pathname-only check
 *   - Anything that parses to a different origin than our synthetic base
 *
 * @param path - The path to validate
 * @returns true if path is a valid internal redirect target
 */
const INTERNAL_BASE = 'http://internal.localhost';

export function isValidInternalRedirect(path: string | null | undefined): path is string {
  if (!path) {
    return false;
  }
  if (!path.startsWith('/')) {
    return false;
  }
  if (path.startsWith('//') || path.startsWith('/\\')) {
    return false;
  }
  try {
    const url = new URL(path, INTERNAL_BASE);
    if (url.origin !== INTERNAL_BASE) {
      return false;
    }
    return url.pathname === '/internal' || url.pathname.startsWith('/internal/');
  } catch {
    return false;
  }
}

/**
 * Gets the redirect destination from multiple sources with priority:
 * 1. React Router location state (from ProtectedRoute)
 * 2. URL query parameter (?redirect=)
 * 3. This tab's Encompass Processing Requests return, when present
 * 4. localStorage (ordinary portal OAuth flow, unchanged)
 * 5. Default (/internal)
 *
 * @param options.locationState - React Router location.state object
 * @param options.searchParams - URLSearchParams from location.search
 * @param options.checkLocalStorage - Whether to check localStorage (for OAuth callback)
 * @param options.consumeProcessingRequestRedirect - False only while rendering HomeRoute;
 * that component clears the integration return after committing navigation.
 * @returns The validated redirect destination
 */
export function getRedirectDestination(options: {
  locationState?: { from?: { pathname?: string; search?: string; hash?: string } } | null;
  searchParams?: URLSearchParams;
  checkLocalStorage?: boolean;
  consumeProcessingRequestRedirect?: boolean;
}): string {
  const {
    locationState,
    searchParams,
    checkLocalStorage = false,
    consumeProcessingRequestRedirect = true
  } = options;

  // 1. Check React Router state (highest priority - from ProtectedRoute).
  // Preserve search and hash so email deep links round-trip through login.
  const from = locationState?.from;
  if (from?.pathname) {
    const stateRedirect = `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
    if (isValidInternalRedirect(stateRedirect)) {
      if (checkLocalStorage && consumeProcessingRequestRedirect) {
        clearProcessingRequestRedirect();
      }
      return stateRedirect;
    }
  }

  // 2. Check URL query parameter
  const queryRedirect = searchParams?.get('redirect');
  if (isValidInternalRedirect(queryRedirect)) {
    if (checkLocalStorage && consumeProcessingRequestRedirect) {
      clearProcessingRequestRedirect();
    }
    return queryRedirect;
  }

  // 3. Restore the integration's tab-specific return, then the ordinary OAuth return.
  if (checkLocalStorage) {
    const processingRedirect = readProcessingRequestRedirect();
    if (processingRedirect !== null) {
      if (consumeProcessingRequestRedirect) {
        clearProcessingRequestRedirect();
      }
      return processingRedirect;
    }
    const storedRedirect = localStorage.getItem('oauth_redirect');
    if (storedRedirect) {
      localStorage.removeItem('oauth_redirect');
      if (isValidInternalRedirect(storedRedirect)) {
        return storedRedirect;
      }
    }
  }

  // Default
  return DEFAULT_DESTINATION;
}

/**
 * Stores redirect destination before OAuth (which leaves the app).
 * Only stores valid internal paths that aren't the default.
 *
 * @param destination - The destination to store
 */
export function storeOAuthRedirect(destination: string): void {
  if (storeProcessingRequestRedirect(destination)) {
    return;
  }
  // A new ordinary login in this tab supersedes an abandoned processing login.
  clearProcessingRequestRedirect();
  if (isValidInternalRedirect(destination) && destination !== DEFAULT_DESTINATION) {
    localStorage.setItem('oauth_redirect', destination);
  }
}
